import { useEffect } from 'react';

const META_PIXEL_ID = '2314996429237093';
const FBEVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';

// Guards against double initialization (React StrictMode mounts twice in dev).
let initialized = false;

interface FbqFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
}

interface PixelWindow extends Window {
  fbq?: FbqFunction;
  _fbq?: FbqFunction;
}

function ensureFbq(): FbqFunction | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;

  const w = window as PixelWindow;
  if (w.fbq) return w.fbq;

  const fbq: FbqFunction = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue?.push(args);
    }
  };

  fbq.queue = [];
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';

  w.fbq = fbq;
  if (!w._fbq) w._fbq = fbq;

  // Append to <head> instead of inserting before the first <script>: in
  // sandboxed/iframe previews there may be no script node to anchor to.
  const script = document.createElement('script');
  script.async = true;
  script.src = FBEVENTS_SRC;
  (document.head || document.documentElement).appendChild(script);

  return fbq;
}

// Initializes the Meta (Facebook) Pixel once and fires the initial PageView.
export function useMetaPixel() {
  useEffect(() => {
    if (initialized) return;

    const fbq = ensureFbq();
    if (!fbq) return;

    initialized = true;
    fbq('init', META_PIXEL_ID);
    fbq('track', 'PageView');
  }, []);
}
